import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useViewMode } from '@/stores/viewMode'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logger } from '@magnetar/utils'
import { Settings, Edit, Trash, Send } from 'lucide-react'
import { AdImageBatch } from '@/hooks/useAdImageBatches'
import { DeleteAlert } from '@/components/DeleteAlert'
import { useAuth } from '@/hooks/useAuth'
import { adImageBatchService } from '@/services/ad_image_batch_service'

interface ActionCellProps {
  batch: AdImageBatch
  onRefresh?: () => void
}

export default function ActionCell({ batch, onRefresh }: ActionCellProps) {
  const navigate = useNavigate()
  const { mode, id: viewModeId } = useViewMode()
  const { token } = useAuth()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // 依當前 scope 組 URL 前綴
  const scopePrefix = mode === 'provider' && viewModeId ? `/provider/${viewModeId}` : '/admin'
  const batchBasePath = `${scopePrefix}/ad-image-batches/${batch.batch_id}`

  const handleDelete = async () => {
    if (!token) return

    try {
      const result = await adImageBatchService.deleteBatch(batch.batch_id, token)
      if (result.success) {
        onRefresh?.()
        setDeleteDialogOpen(false)
      }
    } catch (error) {
      logger.error('刪除失敗', error)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">開啟選單</span>
            <Settings className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>操作</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(`${batchBasePath}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            編輯批次
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`${batchBasePath}/apply`)}>
            <Send className="mr-2 h-4 w-4" />
            套用設定
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600" onClick={() => setDeleteDialogOpen(true)}>
            <Trash className="mr-2 h-4 w-4" />
            刪除批次
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteAlert
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title={`確定要刪除「${batch.batch_name}」嗎？`}
        description="此操作無法復原，將一併移除批次內的所有項目與套用記錄。"
      />
    </>
  )
}
